import { Emotion } from '@emotion/css/types/create-instance';
import { CSSInterpolation, InterpolationPrimitive, SerializedStyles } from '@emotion/serialize';

const isSerializedStyles = (primitive: InterpolationPrimitive): primitive is SerializedStyles =>
  Boolean(
    typeof primitive === 'object' &&
      primitive &&
      'styles' in primitive &&
      typeof primitive.styles === 'string',
  );

const fixInterpolationPrimitive = (primitive: InterpolationPrimitive) => {
  // https://github.com/emotion-js/emotion/issues/2826
  if (isSerializedStyles(primitive) && primitive.styles && !primitive.styles.endsWith(';')) {
    // eslint-disable-next-line no-param-reassign -- It's safe to mutate this because styles are only serialized when they are passed directly to `css`.
    primitive.styles += ';';
  }
};

// `Array.isArray` does not properly narrow readonly arrays.
// See https://github.com/microsoft/TypeScript/issues/17002
const isArray = Array.isArray as unknown as (arg: unknown) => arg is readonly unknown[];

const fixInterpolation = (interpolation: TemplateStringsArray | CSSInterpolation): void => {
  if (isArray(interpolation)) {
    interpolation.forEach(fixInterpolation);
  } else {
    fixInterpolationPrimitive(interpolation);
  }
};

type CssFunction = Emotion['css'];

export const fixCss =
  (css: CssFunction): CssFunction =>
  (...args) => {
    // These are split up only to simplify typing the parameter of `fixInterpolation`.
    const [first, ...rest] = args;
    fixInterpolation(first);
    fixInterpolation(rest);

    // A cast is required due to `css` being overloaded.
    return css(...(args as Parameters<CssFunction>));
  };
